import { InventoryItem } from '../types/inventory';

// Minimal oil shape — both FormulaOil (orders) and FormulaItem (products) satisfy it.
export interface CostOil {
  name: string;
  ml: number;
}

export interface CostLine {
  name: string;
  ml: number;
  unit_cost_per_ml: number | null; // null when the oil has no priced cost
  line_cost: number;               // ml × unit_cost_per_ml (0 when unpriced)
  hasPricing: boolean;
}

export interface FormulationCost {
  breakdown: CostLine[];
  subtotal: number;
  /**
   * Cost of goods, equal to `subtotal`. No tax is added: this is an internal
   * raw-material figure, and GST is a customer-facing concept that has no place
   * in cost-of-goods tracking.
   */
  total: number;
  missingPricing: string[]; // oils with no cost set / absent from inventory
  isComplete: boolean;      // false when any oil is missing pricing
}

/**
 * Cost a list of oils against the current inventory. An oil is considered
 * "priced" only when it exists in inventory with a unit_cost_per_ml > 0; oils
 * priced at 0 or absent from inventory are surfaced in `missingPricing` rather
 * than silently counted as free. Blank oil rows (no name) are ignored so the
 * helper is safe to feed from a formula editor mid-typing.
 */
export function calculateFormulationCost(
  oils: CostOil[],
  inventory: InventoryItem[]
): FormulationCost {
  const costByOil = new Map<string, number | null>();
  for (const item of inventory) {
    costByOil.set(item.oil_name, item.unit_cost_per_ml);
  }

  const breakdown: CostLine[] = [];
  const missingPricing: string[] = [];
  let subtotal = 0;

  for (const oil of oils) {
    const name = (oil.name ?? '').trim();
    if (!name) continue;

    const ml = Number(oil.ml) || 0;
    const cost = costByOil.has(name) ? costByOil.get(name) ?? null : null;
    const hasPricing = cost != null && cost > 0;
    const line_cost = hasPricing ? ml * (cost as number) : 0;

    if (hasPricing) subtotal += line_cost;
    else if (!missingPricing.includes(name)) missingPricing.push(name);

    breakdown.push({ name, ml, unit_cost_per_ml: cost, line_cost, hasPricing });
  }

  return {
    breakdown,
    subtotal,
    total: subtotal,
    missingPricing,
    isComplete: missingPricing.length === 0,
  };
}
